import React from 'react';
import { FormattedMessage } from 'react-intl';

import { IdelinkModuleTheme, useIdelinkTheme } from '../../configs/IdeLinkTheme';
import { NavbarModuleItem } from '../../../libs/IdeLink.Web.Common/src/components/NavBar/components/NavBarItem/NavBarItem';
import { Module } from '../Module';
import { RouteItem } from '../../../libs/IdeLink.Web.Common/src/types/RouteItem';
import { PrivateRoute } from '../../routes/PrivateRoute';
import { AuditAnalysisPageLoading } from './Pages/AuditAnalysis/LoadableAuditAnalysis';
import { AuditAutoCheckCreatePageLoading } from './Pages/AuditAutoChecks/AuditAutoCheckCreatePage/AuditAutoCheckCreatePageLoading';
import { AuditAutoCheckDetailsPageLoading } from './Pages/AuditAutoChecks/AuditAutoCheckDetailsPage/AuditAutoCheckDetailsPageLoading';
import { AuditAutoCheckListPageLoading } from './Pages/AuditAutoChecks/AuditAutoCheckListPage/AuditAutoCheckListPageLoading';
import { AuditListPageLoading } from './Pages/Audits/AuditListPage/AuditListPageLoading';
import { AuditThemeCreatePageLoading } from './Pages/AuditThemes/AuditThemeCreatePage/AuditThemeCreatePageLoading';
import { AuditThemeDetailsPageLoading } from './Pages/AuditThemes/AuditThemeDetailsPage/AuditThemeDetailsPageLoading';
import { AuditThemeListPageLoading } from './Pages/AuditThemes/AuditThemeListPage/AuditThemeListPageLoading';
import { AuditsPermissions } from './Services/AuditsPermissions';
import { AuditTagsListPageLoading } from './Pages/AuditTags/AuditTagsListPage/AuditTagsListPageLoading';
import { AuditTagsCreatePageLoading } from './Pages/AuditTags/AuditTagsCreatePage/AuditTagsCreatePageLoading';
import { AuditTagsDetailsPageLoading } from './Pages/AuditTags/AuditTagsDetailsPage/AuditTagsDetailsPageLoading';
import { AuditDetailPageLoading } from './Pages/Audits/AuditDetailPage/AuditDetailPageLoading';
import { useIsPermissionsInitialized } from '../../../libs/IdeLink.Web.Common/src/Hooks/useIsPermissionsInitialized';

export class AuditsModule {
    public static AUDIT_LIST: string = '/audits-history';
    public static AUDIT_DETAILS: string = '/audits/:shopId/:id';

    public static AUDIT_THEME_LIST: string = '/audits-themes';
    public static AUDIT_THEME_CREATE: string = '/audits-themes/create';
    public static AUDIT_THEME_DETAILS: string = '/audits-themes/:id';

    public static AUDIT_AUTOCHECK_LIST: string = '/audits-autochecks';
    public static AUDIT_AUTOCHECK_CREATE: string = '/audits-autochecks/create';
    public static AUDIT_AUTOCHECK_DETAILS: string = '/audits-autochecks/:id';

    public static AUDIT_ANALYSIS: string = '/audits-analysis';

    public static AUDIT_TAGS: string = '/audits-tags';
    public static AUDIT_TAG_CREATE: string = '/audits-tags/create';
    public static AUDIT_TAG_DETAILS: string = '/audits-tags/:id';

    //#region //* AUDIT HISTORY ROUTES

    private static getAuditListRoute = (): RouteItem => ({
        render: PrivateRoute,
        props: {
            path: AuditsModule.AUDIT_LIST,
            component: AuditListPageLoading,
        },
    });

    private static getAuditDetailRoute = (): RouteItem => ({
        render: PrivateRoute,
        props: {
            path: AuditsModule.AUDIT_DETAILS,
            component: AuditDetailPageLoading,
        },
    });

    public static toAuditDetailsRoute = (shopId: string, auditId: string): string => {
        return `${AuditsModule.AUDIT_DETAILS}`.replace(':shopId', shopId).replace(':id', auditId);
    };

    //#endregion

    //#region //* AUDIT THEME ROUTES

    private static getAuditThemeListRoute = (): RouteItem => ({
        render: PrivateRoute,
        props: {
            path: AuditsModule.AUDIT_THEME_LIST,
            component: AuditThemeListPageLoading,
        },
    });
    private static getAuditThemeCreateRoute = (): RouteItem => ({
        render: PrivateRoute,
        props: {
            path: AuditsModule.AUDIT_THEME_CREATE,
            component: AuditThemeCreatePageLoading,
        },
    });
    private static getAuditThemeDetailsRoute = (): RouteItem => ({
        render: PrivateRoute,
        props: {
            path: AuditsModule.AUDIT_THEME_DETAILS,
            component: AuditThemeDetailsPageLoading,
        },
    });
    public static auditThemeDetailsRoute = (auditThemeId: string): string => {
        return `${AuditsModule.AUDIT_THEME_DETAILS}`.replace(':id', auditThemeId);
    };

    //#endregion

    //#region //* AUDIT ANALYSIS
    private static getAuditAnalysisRoute = (): RouteItem => ({
        render: PrivateRoute,
        props: {
            path: AuditsModule.AUDIT_ANALYSIS,
            component: AuditAnalysisPageLoading,
        },
    });

    //#endregion

    //#region //* AUDIT AUTOCHECK ROUTES

    private static getAuditAutoCheckListRoute = (): RouteItem => ({
        render: PrivateRoute,
        props: {
            path: AuditsModule.AUDIT_AUTOCHECK_LIST,
            component: AuditAutoCheckListPageLoading,
        },
    });
    private static getAuditAutoCheckCreateRoute = (): RouteItem => ({
        render: PrivateRoute,
        props: {
            path: AuditsModule.AUDIT_AUTOCHECK_CREATE,
            component: AuditAutoCheckCreatePageLoading,
        },
    });
    private static getAuditAutoCheckDetailsRoute = (): RouteItem => ({
        render: PrivateRoute,
        props: {
            path: AuditsModule.AUDIT_AUTOCHECK_DETAILS,
            component: AuditAutoCheckDetailsPageLoading,
        },
    });
    public static auditAutoCheckDetailsRoute = (auditAutoCheckId: string): string => {
        return `${AuditsModule.AUDIT_AUTOCHECK_DETAILS}`.replace(':id', auditAutoCheckId);
    };

    //#endregion

    //#region //* AUDIT TAGS
    private static getAuditTagsRoute = (): RouteItem => ({
        render: PrivateRoute,
        props: {
            path: AuditsModule.AUDIT_TAGS,
            component: AuditTagsListPageLoading,
        },
    });

    private static getAuditTagCreateRoute = (): RouteItem => ({
        render: PrivateRoute,
        props: {
            path: AuditsModule.AUDIT_TAG_CREATE,
            component: AuditTagsCreatePageLoading,
            hasAccess: () => AuditsPermissions.Admin.canCreate,
        },
    });

    private static getAuditTagDetailsRoute = (): RouteItem => ({
        render: PrivateRoute,
        props: {
            path: AuditsModule.AUDIT_TAG_DETAILS,
            component: AuditTagsDetailsPageLoading,
            hasAccess: () => AuditsPermissions.Admin.canRead,
        },
    });

    public static auditTagDetailsRoute = (auditTagId: string): string => {
        return `${AuditsModule.AUDIT_TAG_DETAILS}`.replace(':id', auditTagId);
    };

    //#endregion

    public static getAuditsRoutes = (): Array<RouteItem> => {
        return [
            AuditsModule.getAuditThemeListRoute(),
            AuditsModule.getAuditThemeCreateRoute(),
            AuditsModule.getAuditThemeDetailsRoute(),

            AuditsModule.getAuditListRoute(),
            AuditsModule.getAuditDetailRoute(),

            AuditsModule.getAuditAutoCheckListRoute(),
            AuditsModule.getAuditAutoCheckCreateRoute(),
            AuditsModule.getAuditAutoCheckDetailsRoute(),

            AuditsModule.getAuditAnalysisRoute(),

            AuditsModule.getAuditTagsRoute(),
            AuditsModule.getAuditTagCreateRoute(),
            AuditsModule.getAuditTagDetailsRoute(),
        ];
    };

    public static preloadAuditThemeListPage = () => {
        AuditThemeListPageLoading.preload();
    };

    public static getAuditsModuleNavbar = (theme: IdelinkModuleTheme): NavbarModuleItem => {
        return {
            icon: theme.navbarIcon,
            label: <FormattedMessage id="navbar.audits" />,
            route: AuditsModule.AUDIT_THEME_LIST,
            onMouseOver: () => AuditsModule.preloadAuditThemeListPage(),
            childRoutes: [
                AuditsModule.AUDIT_LIST,
                AuditsModule.AUDIT_AUTOCHECK_LIST,
                AuditsModule.AUDIT_ANALYSIS,
                AuditsModule.AUDIT_TAGS,
            ],
        };
    };
}

export const IdkAuditsModule = () => {
    const ideLinkTheme = useIdelinkTheme();
    //this module need the permissions to load
    if (!useIsPermissionsInitialized()) {
        return null;
    }
    return (
        <Module
            moduleKey="AuditsModule"
            permissionsItems={[AuditsPermissions.getAuditsTab()]}
            routes={AuditsModule.getAuditsRoutes()}
            navBarItems={[AuditsModule.getAuditsModuleNavbar(ideLinkTheme.audits)]}
        />
    );
};
